import React, { useRef, useState } from 'react';
import { Camera, UserRound } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

export const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export async function readProfilePhoto(file: File): Promise<string> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Escolha uma foto PNG, JPEG ou WebP.');
  if (file.size > PROFILE_PHOTO_MAX_BYTES) throw new Error('A foto deve ter no máximo 2 MB.');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler a foto.'));
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Foto inválida.'));
    reader.readAsDataURL(file);
  });
}
export const ProfilePhotoInput: React.FC<{ value: string | null; onChange: (value: string | null) => void; disabled?: boolean; onReadingChange?: (reading: boolean) => void }> = ({ value, onChange, disabled, onReadingChange }) => {
  const { isDark } = useTheme();
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const request = useRef(0);
  return <div className="space-y-2">
    <div className="flex items-center gap-4">
      <div className={`h-16 w-16 shrink-0 overflow-hidden rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-slate-100'}`}>
        {value ? <img src={value} alt="Foto do perfil" className="h-full w-full object-cover" /> : <UserRound className="h-7 w-7 text-sminy-primary" />}
      </div>
      <div className="space-y-2">
        <label className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${isDark ? 'border-gray-600 text-gray-200' : 'border-slate-300 text-slate-700'}`}>
          <Camera className="h-4 w-4" />{reading ? 'Lendo foto...' : 'Escolher foto'}
          <input aria-label="Foto do perfil" type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" disabled={disabled || reading} onChange={async e => {
            const file = e.target.files?.[0]; e.target.value = ''; if (!file) return;
            const id = ++request.current; setError(''); setReading(true); onReadingChange?.(true);
            try { const data = await readProfilePhoto(file); if (id === request.current) onChange(data); }
            catch (err) { if (id === request.current) setError((err as Error).message); }
            finally { if (id === request.current) { setReading(false); onReadingChange?.(false); } }
          }} />
        </label>
        {value && <button type="button" disabled={disabled || reading} className="ml-2 text-sm text-sminy-primary" onClick={() => onChange(null)}>Remover foto</button>}
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>PNG, JPEG ou WebP, até 2 MB.</p>
      </div>
    </div>
    {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
  </div>;
};
